import { GetTelemetryHistoryResponse } from '@eppendorf/vnls-device-service-utils';
import { Switch } from '@eppendorf/vnls-react-components';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HistoryChartV2Wrapper } from '$features/monitoring/history-charts-v2/history-chart-v2-wrapper';
import { useHistoryChartsConfig } from '$features/monitoring/history-charts-v2/useHistoryChartsConfig';
import { toStabilityReportChartData } from '$features/report-hub/stability-report/stability-report-chart-data';
import { useGetStabilityReportChart } from '$features/report-hub/stability-report/stability-report-charts.api';
import { StabilityReportDetailModel } from '$features/report-hub/stability-report/stability-report.types';

/** A successfully loaded archive may not contain data for a legacy parameter. */
const EMPTY_TELEMETRY_HISTORY: GetTelemetryHistoryResponse = {
  telemetryId: '',
  unit: null,
  telemetryFunctionName: null,
  telemetryFunctionHistoryIsIncomplete: false,
  deviceStateHistoryIsIncomplete: false,
  offlinePeriods: [],
  bucketInterval: '',
  bucketIntervalMs: 0,
  historyData: [],
};

interface StabilityReportParameterChartProps {
  reportId: string;
  parameterName: string;
  telemetryId?: string;
  fractionalDigits?: number;
  startDate: string;
  endDate: string;
  syncId: string;
}

function StabilityReportParameterChart({
  reportId,
  parameterName,
  telemetryId,
  fractionalDigits,
  startDate,
  endDate,
  syncId,
}: StabilityReportParameterChartProps) {
  const { data, isError, isLoading } = useGetStabilityReportChart(
    reportId,
    parameterName,
    telemetryId,
  );
  const chartData = useMemo(
    () => (data ? toStabilityReportChartData(data, { startDate, endDate }) : undefined),
    [data, startDate, endDate],
  );

  return (
    <HistoryChartV2Wrapper
      data={chartData ?? (!isError && !isLoading ? EMPTY_TELEMETRY_HISTORY : undefined)}
      isError={isError}
      isLoading={isLoading}
      parameterName={parameterName}
      fractionalDigits={fractionalDigits ?? data?.fractionalDigits}
      syncId={syncId}
      syncByTimestamp
    />
  );
}

export interface StabilityReportHistoryChartsProps {
  report: StabilityReportDetailModel;
}

export function StabilityReportHistoryCharts({
  report,
}: StabilityReportHistoryChartsProps) {
  const { t } = useTranslation();
  const syncId = useId();
  const { showThresholdRefs, toggleShowThresholdRefs } = useHistoryChartsConfig();

  if (!report.parameters?.length) {
    return (
      <span className="color-gray-500">{t('stabilityDetail.noParametersAvailable')}</span>
    );
  }

  return (
    <>
      <div className="flex flex__dir--row flex__jc--flex-end gap-s m-x-m m-y-l">
        <span>
          {t('monitoringDetail.historyChartsDetail.showAlertThresholdsToggleLabel')}
        </span>
        <Switch
          checked={showThresholdRefs}
          onCheckedChange={(state) => toggleShowThresholdRefs(state)}
        />
      </div>
      {report.parameters.map((parameter) => (
        <StabilityReportParameterChart
          key={parameter.telemetryId ?? parameter.name}
          reportId={report.id}
          parameterName={parameter.name}
          telemetryId={parameter.telemetryId}
          fractionalDigits={parameter.fractionalDigits}
          startDate={report.startDate}
          endDate={report.endDate}
          syncId={syncId}
        />
      ))}
    </>
  );
}
